'use client';

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  EdgeLambdaApiError,
  edgeLambdaKeys,
  setThreatReportShared,
  type ThreatReportSummary,
} from '@/shared/cors-edge-lambda-api';
import { threatReportIdentity } from '@/shared/threat-report-sharing';
import { useCaseFolderName } from './useCaseFolderName';
import { useCellServiceSession } from './useCellServiceSession';

function withShared(reports: ThreatReportSummary[] | undefined, target: ThreatReportSummary, shared: boolean) {
  if (!reports) return reports;
  const id = threatReportIdentity(target);
  return reports.map((report) =>
    threatReportIdentity(report) === id ? { ...report, shared } : report,
  );
}

export function sharingErrorMessage(error: unknown): { title: string; description: string } {
  const status = error instanceof EdgeLambdaApiError ? error.status : null;
  if (status === 403) {
    return { title: 'Only the report creator can change sharing.', description: '' };
  }
  if (status === 409) {
    return { title: 'Only a completed report can be shared.', description: '' };
  }
  return { title: "Couldn't update sharing. Try again.", description: '' };
}

export function sharingSuccessMessage(shared: boolean, title: string): { title: string; description: string } {
  if (shared) {
    return {
      title: 'Threat report shared',
      description: `Everyone on this case in your organization can open "${title}".`,
    };
  }
  return {
    title: 'Threat report is private',
    description: `Only you can open "${title}".`,
  };
}

/** Share or unshare one version, updating both report lists. */
export function useThreatReportSharing(
  caseId: string | undefined | null,
): UseMutationResult<void, Error, { report: ThreatReportSummary; shared: boolean }> {
  const queryClient = useQueryClient();
  const { cellId, accessToken } = useCellServiceSession();
  const trimmedCaseId = caseId?.trim() || '';
  // Must be the key ``useThreatReportVersions`` is subscribed under, folder segment included,
  // or the optimistic update and the invalidation below miss the live query (TR-4714).
  const caseFolderName = useCaseFolderName(trimmedCaseId);
  const ownKey = edgeLambdaKeys.threatReportVersions(cellId, trimmedCaseId, caseFolderName);
  const sharedKey = edgeLambdaKeys.threatReportShared(cellId, trimmedCaseId);

  return useMutation({
    mutationFn: ({ report, shared }) =>
      setThreatReportShared({
        cellId: cellId as string,
        accessToken: accessToken as string,
        caseId: trimmedCaseId,
        chatKey: report.chatKey,
        epochTimeSecs: report.epochTimeSecs,
        shared,
      }),
    onMutate: async ({ report, shared }) => {
      await queryClient.cancelQueries({ queryKey: ownKey });
      await queryClient.cancelQueries({ queryKey: sharedKey });
      const previousOwn = queryClient.getQueryData<ThreatReportSummary[]>(ownKey);
      const previousShared = queryClient.getQueryData<ThreatReportSummary[]>(sharedKey);
      queryClient.setQueryData<ThreatReportSummary[]>(ownKey, (current) => withShared(current, report, shared));
      queryClient.setQueryData<ThreatReportSummary[]>(sharedKey, (current) => {
        const id = threatReportIdentity(report);
        const rows = current ?? [];
        if (shared) {
          if (rows.some((row) => threatReportIdentity(row) === id)) return withShared(rows, report, true);
          return [{ ...report, shared: true }, ...rows];
        }
        return rows.filter((row) => threatReportIdentity(row) !== id);
      });
      return { previousOwn, previousShared };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      queryClient.setQueryData(ownKey, context.previousOwn);
      queryClient.setQueryData(sharedKey, context.previousShared);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ownKey });
      void queryClient.invalidateQueries({ queryKey: sharedKey });
    },
  });
}
